import { useQuery } from "@apollo/client/react";
import { useCallback, useMemo } from "react";

import { useCachedFilterFields } from "components/Filters/hooks";
import { SortOption } from "components/Filters/types";
import useFiltersVariables from "components/Filters/useFiltersVariables";
import useMakeFiltersData from "components/Filters/useMakeFiltersData";

import { FILTER_TYPE, INITIAL_SORT_DIRECTION, INITIAL_SORT_OPTION } from "../../constants";
import {
  EXCLUDED_FILTER_FIELDS,
  SEARCH_CONTEXTS_SUGGESTIONS_DICTIONARY,
  SORT_ITEM_ORDER,
} from "../constants";
import { GET_CONTEXT_SUGGESTIONS } from "./gql";
import { getFilterKey } from "./helpers";

const useContextsFilters = () => {
  const { filterVariables, filterVariablesLoaded } = useFiltersVariables({
    filterType: FILTER_TYPE,
    initialSortDirection: INITIAL_SORT_DIRECTION,
    initialSortOption: INITIAL_SORT_OPTION,
  });

  const { data, loading, refetch } = useQuery(GET_CONTEXT_SUGGESTIONS, {
    variables: {
      input: {
        fields: null,
        fullTextSearch: filterVariables.searchInput,
        predicates: filterVariables.predicates,
      },
    },
    skip: !filterVariablesLoaded,
  });

  const cachedFiltersData = useCachedFilterFields(data?.searchContextsSuggestions?.fields);

  const { filters, filterOptionsMap } = useMakeFiltersData({
    suggestions: cachedFiltersData,
    dictionary: SEARCH_CONTEXTS_SUGGESTIONS_DICTIONARY,
    excludedFilterFields: EXCLUDED_FILTER_FIELDS,
  });

  const sortOptions = useMemo((): SortOption[] => {
    return (
      cachedFiltersData
        .filter(
          (field) =>
            field.orderable && field.name !== "slug" && SORT_ITEM_ORDER.includes(field.name)
        )
        .map((field) => {
          return {
            value: field.name,
            label: getFilterKey(field.name),
          };
        })
        .sort((a, b) => SORT_ITEM_ORDER.indexOf(a.value) - SORT_ITEM_ORDER.indexOf(b.value)) || []
    );
  }, [cachedFiltersData]);

  // v3's useQuery populated `called` as false only while this query was still skipped,
  // so the `if (!called)` guard this used to have only ever fired a refetch during that
  // same window - useQuery has no v4 equivalent to `called`, but `skip` here is exactly
  // that same condition, so it replaces it directly.
  const refreshSuggestions = useCallback(
    (fields?: string[]) => {
      if (!filterVariablesLoaded) {
        refetch?.({
          input: {
            fields: fields || null,
            fullTextSearch: filterVariables.searchInput,
            predicates: filterVariables.predicates,
          },
        });
      }
    },
    [filterVariablesLoaded, filterVariables.searchInput, filterVariables.predicates, refetch]
  );

  return {
    filters,
    filtersMap: filterOptionsMap,
    refetch,
    loading,
    itemsCount: data?.searchContextsSuggestions.filteredCount || 0,
    sortOptions,
    refreshSuggestions,
  };
};

export default useContextsFilters;
